import { useState, useMemo, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, X, SlidersHorizontal, Sparkles } from "lucide-react";
import ProductCard from "@/components/ProductCard";
import products, { categories } from "@/data/products";

// Simple fade-in animation for products
const FadeIn = ({ children, delay = 0 }) => {
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  return (
    <div 
      className={`transition-all duration-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}
    >
      {children}
    </div>
  );
};

export default function Products() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get("category") || "All");
  const [showFilters, setShowFilters] = useState(false);
  const [visibleCount, setVisibleCount] = useState(20); // Initial batch

  useEffect(() => {
    const cat = searchParams.get("category");
    if (cat) setSelectedCategory(cat);
  }, [searchParams]);

  const filtered = useMemo(() => {
    return products.filter(p => {
      const q = search.toLowerCase();
      const matchSearch = search === "" ||
        p.name.toLowerCase().includes(q) ||
        p.salt.toLowerCase().includes(q) ||
        p.description.toLowerCase().includes(q);
      const matchCategory = selectedCategory === "All" || p.category === selectedCategory;
      return matchSearch && matchCategory;
    });
  }, [search, selectedCategory]);

  // Reset visible count when filters change
  useEffect(() => {
    setVisibleCount(20);
  }, [search, selectedCategory]);

  // Load more products on scroll
  const handleScroll = useCallback(() => {
    if (visibleCount >= filtered.length) return;
    
    const scrollY = window.scrollY;
    const windowHeight = window.innerHeight;
    const docHeight = document.documentElement.scrollHeight;
    
    if (scrollY + windowHeight > docHeight - 500) {
      setVisibleCount(prev => Math.min(prev + 12, filtered.length));
    }
  }, [visibleCount, filtered.length]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const clearFilters = () => {
    setSearch("");
    setSelectedCategory("All");
    setSearchParams({});
  };

  const hasFilters = search || selectedCategory !== "All";
  const visibleProducts = filtered.slice(0, visibleCount);

  return (
    <div data-testid="products-page" className="min-h-screen pt-16 md:pt-0">
      {/* Header */}
      <section className="pt-24 md:pt-32 pb-6 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 15 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.4 }}
          >
            <p className="text-xs font-bold tracking-[0.2em] text-[#2DD4BF] uppercase mb-2">Product Catalog</p>
            <h1 className="text-3xl md:text-4xl font-extrabold text-white mb-2">
              Our Products
            </h1>
            <p className="text-[#94A3B8] text-sm max-w-xl">
              Search by <strong className="text-white">product name</strong> or <strong className="text-white">salt/generic name</strong>
            </p>
          </motion.div>
        </div>
      </section>

      {/* Search & Filters */}
      <section className="sticky top-0 z-30 px-6 py-3 border-b border-white/5 bg-[#020617]/95 backdrop-blur-lg">
        <div className="max-w-7xl mx-auto">
          {/* Search bar */}
          <div className="flex items-center gap-2">
            <div className="flex-1 relative">
              <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
              <input
                type="text"
                data-testid="product-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by product or salt name..."
                className="w-full h-10 pl-10 pr-4 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder:text-[#94A3B8]/50 focus:outline-none focus:border-[#2DD4BF]/50 transition-colors"
              />
              {search && (
                <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-white">
                  <X size={14} />
                </button>
              )}
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              data-testid="toggle-filters-btn"
              className={`h-10 px-3.5 rounded-lg border text-sm font-medium flex items-center gap-1.5 transition-colors ${
                showFilters ? "border-[#2DD4BF] text-[#2DD4BF] bg-[#2DD4BF]/5" : "border-white/10 text-[#94A3B8] hover:border-white/20"
              }`}
            >
              <SlidersHorizontal size={14} />
              <span className="hidden sm:inline text-xs">Filter</span>
            </button>
          </div>

          {/* Filter panels */}
          {showFilters && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="mt-3"
            >
              <div className="flex flex-wrap gap-1.5">
                {["All", ...categories.map(c => c.name)].map(cat => (
                  <button
                    key={cat}
                    data-testid={`filter-category-${cat.toLowerCase().replace(/\s/g,'-')}`}
                    onClick={() => setSelectedCategory(cat)}
                    className={`px-3 py-1.5 rounded-full text-[11px] font-medium transition-colors ${
                      selectedCategory === cat
                        ? "bg-[#2DD4BF] text-[#022C22]"
                        : "bg-white/5 text-[#94A3B8] hover:bg-white/10 border border-white/5"
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Results count */}
          {hasFilters && (
            <div className="flex items-center gap-2 mt-2 text-xs text-[#94A3B8]">
              <span>{filtered.length} result{filtered.length !== 1 ? 's' : ''}</span>
              <button onClick={clearFilters} className="text-[#2DD4BF] hover:underline ml-auto">
                Clear
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Product Grid */}
      <section className="py-6 px-6 pb-32">
        <div className="max-w-7xl mx-auto">
          {visibleProducts.length > 0 ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4">
                {visibleProducts.map((product, i) => (
                  <FadeIn key={product.id} delay={Math.min(i * 30, 300)}>
                    <ProductCard product={product} />
                  </FadeIn>
                ))}
              </div>
              
              {/* Load more indicator */}
              {visibleCount < filtered.length && (
                <div className="flex justify-center mt-8">
                  <div className="flex items-center gap-2 text-sm text-[#94A3B8]">
                    <div className="w-4 h-4 border-2 border-[#2DD4BF]/30 border-t-[#2DD4BF] rounded-full animate-spin" />
                    <span>Loading more products...</span>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-20" data-testid="no-results">
              <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
                <Search size={24} className="text-[#94A3B8]/40" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">No products found</h3>
              <p className="text-sm text-[#94A3B8] mb-2">Try a different search term</p>
              <p className="text-xs text-[#94A3B8] mb-4">
                <Sparkles size={11} className="inline text-[#2DD4BF] mr-1" />
                Use DrukstAI to ask about any drug
              </p>
              <button onClick={clearFilters} className="px-5 py-2 rounded-full bg-[#2DD4BF] text-[#022C22] text-sm font-bold">
                Clear Filters
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
